import { useEffect, useRef, useState, type ReactNode } from 'react';
import { toZwApiError, type ZwApiError } from '../api/client';
import type { BookingMetadata, BookingPublic, BookingRequest, Slot } from '../api/types';
import { useBooking } from '../hooks/use-booking';
import { useEventType } from '../hooks/use-event-type';
import { useHold } from '../hooks/use-hold';
import { useSlots } from '../hooks/use-slots';
import { labelForErrorCode, type Labels } from '../i18n/labels';
import { ZeitwahlProvider, useZeitwahl } from '../provider';
import {
  addDays,
  dateIn,
  isISODate,
  isISOMonth,
  monthOf,
  monthRange,
  todayIn,
  type ISODate,
  type ISOMonth,
} from '../time/calendar';
import { formatInstantDateTime } from '../time/format';
import { guessTimeZone, isValidTimeZone } from '../time/zones';
import { SelectedTime } from './booking-details';
import { BookingForm, type BookingFormValues, type Prefill } from './booking-form';
import { Confirmation } from './confirmation';
import { EventTypeDetails } from './event-type-details';
import { HoldCountdown } from './hold-countdown';
import { MonthCalendar } from './month-calendar';
import { Chevron, Notice, RetryButton } from './notice';
import { SlotPicker } from './slot-picker';
import { TimeZoneSelect } from './time-zone-select';

export interface BookingFlowProps {
  /** `''` on hosted pages, `/api/booking` behind a site proxy, or the Zeitwahl origin. Inherits from an enclosing `ZeitwahlProvider` when omitted. */
  apiBaseUrl?: string;
  org: string;
  eventType: string;
  widgetKey?: string;
  prefill?: Prefill;
  metadata?: BookingMetadata;
  locale?: string;
  /** Initial IANA zone; defaults to the browser's. The visitor can change it. */
  timezone?: string;
  labels?: Partial<Labels>;
  onBooked?: (booking: BookingPublic, context: { manageUrl: string }) => void;
  onError?: (error: ZwApiError) => void;
  onDateSelected?: (date: ISODate) => void;
  bookedTitle?: ReactNode;
  bookedBody?: ReactNode;
  hideEventTypeDetails?: boolean;
  /** `YYYY-MM-DD` preselected day (deep link). */
  initialDate?: string;
  /** `YYYY-MM` first visible month (deep link). */
  initialMonth?: string;
  /** Restrict to one host (slug). */
  host?: string;
  /** Renders a required consent checkbox with this content. */
  consentText?: ReactNode;
  fetch?: typeof fetch;
  headers?: Record<string, string>;
  className?: string;
}

type Step = 'pick' | 'form' | 'done';

interface FlowNotice {
  kind: 'error' | 'warning' | 'info';
  code: string;
  text: string;
  nextAvailable?: string;
  retryAfterSeconds?: number;
  action?: 'retry' | 'repick';
}

/**
 * The complete visitor flow: calendar + time zone + slots → hold with countdown → details form →
 * confirmation. Wraps its own `ZeitwahlProvider`, so it works standalone or inside one.
 */
export function BookingFlow(props: BookingFlowProps): ReactNode {
  return (
    <ZeitwahlProvider
      apiBaseUrl={props.apiBaseUrl}
      widgetKey={props.widgetKey}
      locale={props.locale}
      labels={props.labels}
      timezone={props.timezone}
      fetch={props.fetch}
      headers={props.headers}
    >
      <BookingFlowInner {...props} />
    </ZeitwahlProvider>
  );
}

function BookingFlowInner(props: BookingFlowProps): ReactNode {
  const {
    org,
    eventType: slug,
    prefill,
    metadata,
    host,
    hideEventTypeDetails,
    onBooked,
    onError,
    onDateSelected,
  } = props;
  const { locale, labels, t, timeZone: defaultTimeZone } = useZeitwahl();

  const [timeZone, setTimeZone] = useState(() =>
    defaultTimeZone && isValidTimeZone(defaultTimeZone) ? defaultTimeZone : guessTimeZone()
  );
  const eventType = useEventType(org, slug);
  const today = todayIn(timeZone);
  const currentMonth = monthOf(today);
  const [month, setMonth] = useState<ISOMonth>(() => {
    const initial = isISOMonth(props.initialMonth)
      ? props.initialMonth
      : isISODate(props.initialDate)
        ? monthOf(props.initialDate)
        : currentMonth;
    return initial < currentMonth ? currentMonth : initial;
  });
  const [selectedDate, setSelectedDate] = useState<ISODate | null>(() =>
    isISODate(props.initialDate) && props.initialDate >= today ? props.initialDate : null
  );
  const range = monthRange(month);
  const slots = useSlots({
    org,
    eventType: slug,
    from: range.from,
    to: range.to,
    tz: timeZone,
    host,
    enabled: !!eventType.data,
  });
  const hold = useHold(org);
  const booking = useBooking(org);

  const [step, setStep] = useState<Step>('pick');
  const [slot, setSlot] = useState<Slot | null>(null);
  const [pendingSlot, setPendingSlot] = useState<Slot | null>(null);
  const [notice, setNotice] = useState<FlowNotice | null>(null);
  const [noticeSeq, setNoticeSeq] = useState(0);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string> | undefined>();
  const lastAction = useRef<(() => void) | null>(null);
  const mainRef = useRef<HTMLDivElement>(null);

  const maxMonth = eventType.data
    ? monthOf(addDays(today, eventType.data.bookingWindowDays))
    : undefined;

  const showNotice = (n: FlowNotice) => {
    setNotice(n);
    setNoticeSeq((s) => s + 1);
  };

  const fail = (error: unknown, phase: 'hold' | 'confirm') => {
    const err = toZwApiError(error);
    onError?.(err);
    const details = err.details;
    const retryAfterSeconds =
      typeof details.retryAfterSeconds === 'number' ? details.retryAfterSeconds : undefined;
    switch (err.code) {
      case 'slot_unavailable': {
        slots.refresh();
        setSlot(null);
        if (phase === 'confirm') {
          hold.reset();
          setStep('pick');
        }
        const next = typeof details.nextAvailable === 'string' ? details.nextAvailable : undefined;
        showNotice({
          kind: 'error',
          code: err.code,
          text: next
            ? t('errorSlotUnavailableNext', { time: formatInstantDateTime(next, locale, timeZone) })
            : t('errorSlotUnavailable'),
          nextAvailable: next,
        });
        break;
      }
      case 'hold_expired':
        slots.refresh();
        hold.reset();
        setSlot(null);
        setStep('pick');
        showNotice({ kind: 'error', code: err.code, text: t('errorHoldExpired') });
        break;
      case 'validation_failed':
        setFieldErrors(details.fields ?? {});
        showNotice({ kind: 'error', code: err.code, text: t('errorValidationFailed') });
        break;
      case 'provider_unavailable':
      case 'confirm_in_progress':
      case 'rate_limited': {
        const seconds = retryAfterSeconds ?? (err.code === 'rate_limited' ? 30 : 10);
        showNotice({
          kind: 'error',
          code: err.code,
          text: labelForErrorCode(labels, err.code, { seconds }),
          retryAfterSeconds: seconds,
          action: 'retry',
        });
        break;
      }
      default:
        showNotice({
          kind: 'error',
          code: err.code,
          text: labelForErrorCode(labels, err.code),
          action: err.code === 'network_error' ? 'retry' : undefined,
        });
    }
  };

  const chooseSlot = async (next: Slot) => {
    setNotice(null);
    setSlot(next);
    setPendingSlot(next);
    lastAction.current = () => void chooseSlot(next);
    try {
      await hold.create({ eventType: slug, start: next.start, timeZone, host });
      setStep('form');
    } catch (error) {
      fail(error, 'hold');
    } finally {
      setPendingSlot(null);
    }
  };

  const submitValues = async (values: BookingFormValues) => {
    const current = hold.hold;
    if (!current) return;
    setNotice(null);
    setFieldErrors(undefined);
    lastAction.current = () => void submitValues(values);
    const body: BookingRequest = {
      holdId: current.id,
      sessionId: hold.sessionId,
      visitor: {
        name: values.name,
        email: values.email,
        phone: values.phone,
        phoneCountry: values.phoneCountry,
        timeZone,
        locale: prefill?.locale ?? locale,
        formatLocale: typeof navigator !== 'undefined' ? navigator.language : undefined,
      },
      answers: values.answers,
      metadata,
    };
    try {
      const result = await booking.submit(body);
      hold.reset();
      setStep('done');
      onBooked?.(result.booking, { manageUrl: result.manageUrl });
    } catch (error) {
      fail(error, 'confirm');
    }
  };

  const goBack = () => {
    void hold.release();
    setSlot(null);
    setNotice(null);
    setFieldErrors(undefined);
    setStep('pick');
  };

  const repick = () => {
    hold.reset();
    slots.refresh();
    setSlot(null);
    setNotice(null);
    setStep('pick');
  };

  const onHoldExpire = () => {
    hold.expire();
    showNotice({ kind: 'error', code: 'hold_expired', text: t('holdExpired'), action: 'repick' });
  };

  const changeMonth = (next: ISOMonth) => {
    setMonth(next);
    if (selectedDate && monthOf(selectedDate) !== next) setSelectedDate(null);
  };

  const selectDate = (date: ISODate) => {
    setSelectedDate(date);
    onDateSelected?.(date);
  };

  const showNextAvailable = (instant: string) => {
    const date = dateIn(timeZone, instant);
    setMonth(monthOf(date));
    setSelectedDate(date);
    setNotice(null);
  };

  useEffect(() => {
    if (step === 'pick')
      mainRef.current
        ?.querySelector<HTMLElement>('.zw-cal-title')
        ?.scrollIntoView?.({ block: 'nearest' });
  }, [step]);

  const rootClass = `zw-root zw-flow zw-flow--${step}${props.className ? ` ${props.className}` : ''}`;

  if (eventType.error) {
    return (
      <div className={rootClass} data-zw-step={step}>
        <Notice kind="error">
          <p>{labelForErrorCode(labels, eventType.error.code)}</p>
          <RetryButton onRetry={eventType.reload} />
        </Notice>
      </div>
    );
  }
  if (!eventType.data) {
    return (
      <div className={rootClass} data-zw-step={step}>
        <p className="zw-loading" role="status">
          {t('loading')}
        </p>
      </div>
    );
  }

  const data = eventType.data;
  const noticeView = notice && (
    <Notice kind={notice.kind}>
      <p>{notice.text}</p>
      {notice.nextAvailable && (
        <button
          type="button"
          className="zw-button zw-button--secondary"
          onClick={() => showNextAvailable(notice.nextAvailable!)}
        >
          {t('errorSlotUnavailableShow')}
        </button>
      )}
      {notice.action === 'retry' && (
        <RetryButton
          key={noticeSeq}
          seconds={notice.retryAfterSeconds}
          onRetry={() => lastAction.current?.()}
        />
      )}
      {notice.action === 'repick' && (
        <button type="button" className="zw-button zw-button--secondary" onClick={repick}>
          {t('holdChooseAgain')}
        </button>
      )}
    </Notice>
  );

  return (
    <div className={rootClass} data-zw-step={step}>
      <div className={`zw-layout${hideEventTypeDetails ? ' zw-layout--single' : ''}`}>
        {!hideEventTypeDetails && (
          <aside className="zw-side">
            <EventTypeDetails eventType={data} locale={locale} />
          </aside>
        )}
        <div className="zw-main" ref={mainRef}>
          {step === 'pick' && (
            <>
              {noticeView}
              {slots.degraded.length > 0 && <Notice kind="warning">{t('slotsDegraded')}</Notice>}
              {slots.error && (
                <Notice kind="error">
                  <p>{labelForErrorCode(labels, slots.error.code)}</p>
                  <RetryButton onRetry={slots.refresh} />
                </Notice>
              )}
              <div className="zw-pick">
                <div className="zw-pick-calendar">
                  <h3 className="zw-subtitle">{t('calendarSelectDate')}</h3>
                  <MonthCalendar
                    month={month}
                    onMonthChange={changeMonth}
                    selected={selectedDate}
                    onSelect={selectDate}
                    availableDates={slots.availableDates}
                    loading={slots.loading}
                    minMonth={currentMonth}
                    maxMonth={maxMonth}
                    today={today}
                    locale={locale}
                  />
                  <TimeZoneSelect value={timeZone} onChange={setTimeZone} locale={locale} />
                </div>
                <SlotPicker
                  date={selectedDate}
                  slots={selectedDate ? (slots.slotsByDate.get(selectedDate) ?? []) : []}
                  timeZone={timeZone}
                  locale={locale}
                  onSelect={(s) => void chooseSlot(s)}
                  selected={slot}
                  pendingSlot={pendingSlot}
                  disabled={hold.status === 'creating'}
                  loading={slots.loading}
                />
              </div>
            </>
          )}

          {step === 'form' && slot && (
            <>
              <div className="zw-form-head">
                <button type="button" className="zw-back" onClick={goBack}>
                  <Chevron direction="left" />
                  {t('formChangeTime')}
                </button>
                <SelectedTime slot={slot} timeZone={timeZone} locale={locale} />
                <HoldCountdown expiresAt={hold.hold?.expiresAt} onExpire={onHoldExpire} />
              </div>
              {noticeView}
              <BookingForm
                questions={data.questions}
                prefill={prefill}
                timeZone={timeZone}
                locale={locale}
                fieldErrors={fieldErrors}
                submitting={booking.status === 'submitting'}
                disabled={hold.status !== 'held'}
                consentText={props.consentText}
                focusOnMount
                onSubmit={(values) => void submitValues(values)}
              />
            </>
          )}

          {step === 'done' && booking.result && (
            <Confirmation
              booking={booking.result.booking}
              manageUrl={booking.result.manageUrl}
              title={props.bookedTitle}
              body={props.bookedBody}
              timeZone={timeZone}
              locale={locale}
            />
          )}
        </div>
      </div>
    </div>
  );
}
